import {
  type UseQueryResult,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  ActionListResponseSchema,
  ActionRunSchema,
  type AlertListQuery,
  type AlertListResponse,
  AlertListResponseSchema,
  type EventDto,
  type EventListResponse,
  EventListResponseSchema,
  formatTagFilter,
  type MetricAggregate,
  type MetricLatestResponse,
  MetricLatestResponseSchema,
  type MetricQueryResponse,
  MetricQueryResponseSchema,
  MetricSeriesListResponseSchema,
  SourceListResponseSchema,
  SourceStatsResponseSchema,
  SourceViewResponseSchema,
} from '@vision360/contracts';
import type { z } from 'zod';
import { ApiError } from '@/lib/api/errors';
import { useApi } from '@/lib/auth/session-context';
import type { WidgetDataState } from './types';

/**
 * The only way widgets read data (TechSpec §9.2 rule 5). Query keys are the
 * contract with the live stream (§7.2): `['metrics', sourceId, …]` is
 * invalidated by a metric batch from that source, `['events', 'list', filter]`
 * receives matching new events.
 */

export const TIME_RANGES = ['1h', '6h', '24h', '7d', '30d', '90d'] as const;
export type TimeRange = (typeof TIME_RANGES)[number];

const HOUR = 3_600_000;
export const RANGE_MS: Record<TimeRange, number> = {
  '1h': HOUR,
  '6h': 6 * HOUR,
  '24h': 24 * HOUR,
  '7d': 7 * 24 * HOUR,
  '30d': 30 * 24 * HOUR,
  '90d': 90 * 24 * HOUR,
};

/** The window slides even when no batch arrives (a quiet source). */
const METRIC_REFRESH_MS = 60_000;

export interface MetricQueryParams {
  sourceId: string;
  name: string;
  tags?: Record<string, string> | undefined;
  range: TimeRange;
  agg?: MetricAggregate | undefined;
  fn?: 'rate' | undefined;
}

export const metricKeys = {
  source: (sourceId: string) => ['metrics', sourceId] as const,
  query: (p: MetricQueryParams) =>
    [
      'metrics',
      p.sourceId,
      'query',
      { name: p.name, tags: p.tags ?? {}, range: p.range, agg: p.agg, fn: p.fn },
    ] as const,
  latest: (sourceId: string, name: string, tags?: Record<string, string>) =>
    ['metrics', sourceId, 'latest', { name, tags: tags ?? {} }] as const,
  series: (sourceId: string) => ['metrics', sourceId, 'series'] as const,
};

export function useMetricQuery(
  p: MetricQueryParams,
  now: () => number = Date.now,
): UseQueryResult<MetricQueryResponse> {
  const api = useApi();
  return useQuery({
    queryKey: metricKeys.query(p),
    queryFn: ({ signal }) => {
      const to = now();
      const search = new URLSearchParams({
        sourceId: p.sourceId,
        name: p.name,
        from: new Date(to - RANGE_MS[p.range]).toISOString(),
        to: new Date(to).toISOString(),
        agg: p.agg ?? 'avg',
      });
      const tags = formatTagFilter(p.tags);
      if (tags) search.set('tags', tags);
      if (p.fn) search.set('fn', p.fn);
      return api.request(`/api/v1/metrics/query?${search}`, {
        schema: MetricQueryResponseSchema,
        signal,
      });
    },
    refetchInterval: METRIC_REFRESH_MS,
    enabled: Boolean(p.sourceId && p.name),
  });
}

export interface MetricLatestParams {
  sourceId: string;
  name: string;
  tags?: Record<string, string> | undefined;
}

function latestQuery(api: ReturnType<typeof useApi>, { sourceId, name, tags }: MetricLatestParams) {
  return {
    queryKey: metricKeys.latest(sourceId, name, tags),
    queryFn: ({ signal }: { signal: AbortSignal }) => {
      const search = new URLSearchParams({ sourceId, name });
      const filter = formatTagFilter(tags);
      if (filter) search.set('tags', filter);
      return api.request(`/api/v1/metrics/latest?${search}`, {
        schema: MetricLatestResponseSchema,
        signal,
      });
    },
    refetchInterval: METRIC_REFRESH_MS,
    enabled: Boolean(sourceId && name),
  };
}

export function useMetricLatest(
  sourceId: string,
  name: string,
  tags?: Record<string, string>,
): UseQueryResult<MetricLatestResponse> {
  const api = useApi();
  return useQuery(latestQuery(api, { sourceId, name, tags }));
}

/** Several `useMetricLatest` at once, in input order — for widgets with a variable number of bindings. */
export function useMetricLatestMany(
  params: readonly MetricLatestParams[],
): UseQueryResult<MetricLatestResponse>[] {
  const api = useApi();
  return useQueries({ queries: params.map((p) => latestQuery(api, p)) });
}

/** Connector views (TechSpec §5): displayable JSON a source publishes at each run. */
export const viewKeys = {
  view: (sourceId: string, name: string) => ['views', sourceId, name] as const,
};

export interface SourceView<T> {
  data: T;
  updatedAt: string;
}

/**
 * `GET /sources/:id/views/:name`, its `data` checked against `schema`. A view the
 * source has not published yet (404) is `null`, so the widget shows its empty state.
 */
export function useSourceView<T>(
  sourceId: string,
  name: string,
  schema: z.ZodType<T>,
): UseQueryResult<SourceView<T> | null> {
  const api = useApi();
  return useQuery({
    queryKey: viewKeys.view(sourceId, name),
    queryFn: async ({ signal }) => {
      try {
        const view = await api.request(
          `/api/v1/sources/${encodeURIComponent(sourceId)}/views/${encodeURIComponent(name)}`,
          { schema: SourceViewResponseSchema, signal },
        );
        return { data: schema.parse(view.data), updatedAt: view.updatedAt };
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    enabled: Boolean(sourceId && name),
  });
}

/** Actions the API can run (step 33); labels come from i18n by id. */
export const ACTIONS_KEY = ['actions'] as const;

export function useActions() {
  const api = useApi();
  return useQuery({
    queryKey: ACTIONS_KEY,
    queryFn: ({ signal }) =>
      api.request('/api/v1/actions', { schema: ActionListResponseSchema, signal }),
    staleTime: 5 * 60_000,
  });
}

/** Runs an action; the answer is its audit row, whose `outcome` may be `error`. */
export function useExecuteAction() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, params }: { id: string; params: Record<string, unknown> }) =>
      api.request(`/api/v1/actions/${encodeURIComponent(id)}/execute`, {
        method: 'POST',
        body: { params },
        schema: ActionRunSchema,
      }),
    // Built-in actions change alerts and sources; their lists re-read on their own otherwise.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: alertKeys.all });
      void queryClient.invalidateQueries({ queryKey: SOURCES_KEY });
    },
  });
}

/** Metric names and tag sets a source has written — for editors. */
export function useMetricSeriesList(sourceId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: metricKeys.series(sourceId ?? ''),
    queryFn: ({ signal }) =>
      api.request(`/api/v1/metrics/series?sourceId=${sourceId}`, {
        schema: MetricSeriesListResponseSchema,
        signal,
      }),
    enabled: Boolean(sourceId),
    staleTime: 5 * 60_000,
  });
}

export interface EventFilter {
  sourceId?: string | undefined;
  kindPrefix?: string | undefined;
  minSeverity?: number | undefined;
  limit: number;
}

export const eventKeys = {
  list: (filter: EventFilter) => ['events', 'list', filter] as const,
};

/** Whether a live event belongs in a list fetched with `filter`. */
export function matchesEventFilter(filter: EventFilter, event: EventDto): boolean {
  if (filter.sourceId && event.sourceId !== filter.sourceId) return false;
  if (filter.kindPrefix && !event.kind.startsWith(filter.kindPrefix)) return false;
  if (filter.minSeverity !== undefined && event.severity < filter.minSeverity) return false;
  return true;
}

export function useEvents(filter: EventFilter): UseQueryResult<EventListResponse> {
  const api = useApi();
  return useQuery({
    queryKey: eventKeys.list(filter),
    queryFn: ({ signal }) => {
      const search = new URLSearchParams({ limit: String(filter.limit) });
      if (filter.sourceId) search.set('sourceId', filter.sourceId);
      if (filter.kindPrefix) search.set('kindPrefix', filter.kindPrefix);
      if (filter.minSeverity !== undefined) search.set('minSeverity', String(filter.minSeverity));
      return api.request(`/api/v1/events?${search}`, { schema: EventListResponseSchema, signal });
    },
  });
}

/** Alerts (TechSpec §12); the stream invalidates `['alerts']` on every alert message. */
export const alertKeys = {
  all: ['alerts'] as const,
  list: (query: AlertListQuery) => ['alerts', 'list', query] as const,
};

export function useAlerts(query: AlertListQuery = {}): UseQueryResult<AlertListResponse> {
  const api = useApi();
  return useQuery({
    queryKey: alertKeys.list(query),
    queryFn: ({ signal }) => {
      const search = new URLSearchParams();
      for (const [key, value] of Object.entries(query)) {
        if (value !== undefined) search.set(key, String(value));
      }
      return api.request(`/api/v1/alerts?${search}`, { schema: AlertListResponseSchema, signal });
    },
  });
}

export const SOURCES_KEY = ['sources'] as const;
export const SOURCE_STATS_KEY = ['sources', 'stats'] as const;

/** Every source, refetched when the stream reports a status change. */
export function useSources() {
  const api = useApi();
  return useQuery({
    queryKey: SOURCES_KEY,
    queryFn: ({ signal }) =>
      api.request('/api/v1/sources', { schema: SourceListResponseSchema, signal }),
    // Staleness is judged against the clock: re-read now and then.
    refetchInterval: 60_000,
  });
}

export function useSourceStats() {
  const api = useApi();
  return useQuery({
    queryKey: SOURCE_STATS_KEY,
    queryFn: ({ signal }) =>
      api.request('/api/v1/sources/stats', { schema: SourceStatsResponseSchema, signal }),
    refetchInterval: 60_000,
  });
}

/**
 * Query result → widget state. `isEmpty` and `lastUpdated` are the widget's
 * judgement of its own data; staleness is the frame's (§9.1.1).
 */
export function toDataState<T, D = T>(
  query: UseQueryResult<T>,
  options: {
    select?: (data: T) => D;
    isEmpty: (data: D) => boolean;
    lastUpdated?: (data: D) => Date | undefined;
  },
): WidgetDataState<D> {
  const retry = () => void query.refetch();
  if (query.data === undefined) {
    if (query.isError) return { status: 'error', data: null, error: query.error, retry };
    return { status: 'loading', data: null };
  }
  const data = options.select ? options.select(query.data) : (query.data as unknown as D);
  const lastUpdated = options.lastUpdated?.(data);
  if (options.isEmpty(data)) return { status: 'empty', data, retry };
  return { status: 'ready', data, retry, ...(lastUpdated ? { lastUpdated } : {}) };
}
